import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAlert } from '../contexts/AlertContext';
import { deckApi } from '../services/deckApi';
import type { GuideMeta, TemplateId } from '../types/deck';

/**
 * 템플릿 작성 › 「md 관리」 탭 (GRK_admin#113 · 2026-10-28 개편) — 템플릿별 가이드 md 판 목록 · 보기(목차 · 본문) · 내려받기 · 새 판 올리기.
 * 판은 서버 공용 grk-deck 에 파일로 있다(지우지 않는다). md 를 고치는 것은 사람이 내 컴퓨터에서 — 시스템은 md 를 바꾸지 않는다.
 * 올리기는 운영 admin 에서만 된다 — 판은 운영 · 개발이 같이 쓴다. 권한 · 준비중은 템플릿 작성 페이지(PptDraft)가 본다.
 * 템플릿은 GRK · RadiARC — 판은 템플릿마다 따로다(2026-10-29). 버전 이름(v1.0 …)도 템플릿마다 따로 센다.
 */

const day = (iso: string) => iso.slice(0, 10);

interface Section { h: string; body: string; ai: 'yes' | 'skip' | 'no' }

/** md → 「## 」 장 단위. 부록 Z(템플릿 base64)는 AI 에 넘기지 않고 사람도 읽지 않는다. 0-0 은 grk-deck 이 건너뛰라고 지시한다 */
function toSections(md: string): Section[] {
  const out: Section[] = [];
  let cur = { h: '머리말', lines: [] as string[] };
  const push = () => {
    const body = cur.lines.join('\n').trim();
    if (cur.h === '머리말' && !body) return;
    const z = cur.h.startsWith('부록 Z');
    out.push({ h: cur.h, body: z ? '내장 파일(템플릿 PPTX 를 글자로 바꿔 넣은 것) — 사람은 읽지 않아도 됩니다. 생략' : body, ai: z ? 'no' : cur.h.startsWith('0-0') ? 'skip' : 'yes' });
  };
  for (const line of md.replace(/\r\n/g, '\n').split('\n')) {
    if (/^## /.test(line)) { push(); cur = { h: line.slice(3).trim(), lines: [] }; } else cur.lines.push(line);
  }
  push();
  return out;
}

/** AI 에 넘기지 않는 장만 표시한다 — 나머지(대부분)는 넘긴다 */
const AI_TAG = { skip: 'AI가 건너뜀', no: 'AI에 안 넘김' };
const Tag: React.FC<{ ai: Section['ai'] }> = ({ ai }) => (ai === 'yes' ? null : (
  <span className="shrink-0 whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{AI_TAG[ai]}</span>
));

// ── md 보기 — 표 · 굵게 · 코드 · 제목 · 목록 · 인용만 그린다(가이드 md 에 쓰이는 것)
type Block =
  | { t: 'h'; lv: number; text: string; id: string }
  | { t: 'p'; text: string }
  | { t: 'list'; items: { lv: number; text: string; ord: string }[] }
  | { t: 'table'; rows: string[][] }
  | { t: 'code'; text: string }
  | { t: 'quote'; text: string }
  | { t: 'hr' };

const cells = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

function parseMd(md: string): Block[] {
  const lines = md.split('\n');
  const out: Block[] = [];
  let para: string[] = [];
  let h = 0;
  const flush = () => { if (para.length) out.push({ t: 'p', text: para.join('\n') }); para = []; };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*```/.test(line)) {
      flush();
      const buf: string[] = [];
      while (++i < lines.length && !/^\s*```/.test(lines[i])) buf.push(lines[i]);
      out.push({ t: 'code', text: buf.join('\n') });
      continue;
    }
    const hm = line.match(/^(#{2,6})\s+(.*)$/);
    if (hm) { flush(); out.push({ t: 'h', lv: hm[1].length, text: hm[2], id: `mdh-${h++}` }); continue; }
    if (/^\s*\|/.test(line)) {
      flush();
      const rows: string[][] = [];
      for (; i < lines.length && /^\s*\|/.test(lines[i]); i++) if (!/^[\s|:-]+$/.test(lines[i])) rows.push(cells(lines[i]));
      i--;
      out.push({ t: 'table', rows });
      continue;
    }
    if (/^>\s?/.test(line)) {
      flush();
      const buf: string[] = [];
      for (; i < lines.length && /^>\s?/.test(lines[i]); i++) buf.push(lines[i].replace(/^>\s?/, ''));
      i--;
      out.push({ t: 'quote', text: buf.join('\n') });
      continue;
    }
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      flush();
      const items: { lv: number; text: string; ord: string }[] = [];
      for (; i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i]); i++) {
        const m = lines[i].match(/^(\s*)([-*]|\d+\.)\s+(.*)$/)!;
        items.push({ lv: Math.min(3, Math.floor(m[1].length / 2)), ord: /\d/.test(m[2]) ? m[2] : '', text: m[3] });
      }
      i--;
      out.push({ t: 'list', items });
      continue;
    }
    if (/^\s*-{3,}\s*$/.test(line)) { flush(); out.push({ t: 'hr' }); continue; }
    if (!line.trim()) { flush(); continue; }
    para.push(line);
  }
  flush();
  return out;
}

const Inline: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean).map((part, i) =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4 ? <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>
        : part.startsWith('`') && part.endsWith('`') && part.length > 2 ? <code key={i} className="rounded bg-slate-100 px-1 py-0.5 text-[0.85em] text-slate-800">{part.slice(1, -1)}</code>
          : <React.Fragment key={i}>{part}</React.Fragment>)}
  </>
);

const MdView: React.FC<{ blocks: Block[] }> = ({ blocks }) => (
  <div className="space-y-3 text-sm leading-relaxed text-slate-700">
    {blocks.map((b, i) => {
      switch (b.t) {
        case 'h': return <h4 key={i} id={b.id} className={`scroll-mt-2 font-semibold text-slate-800 ${b.lv <= 3 ? 'pt-2 text-base' : 'text-sm'}`}><Inline text={b.text} /></h4>;
        case 'p': return <p key={i} className="whitespace-pre-line"><Inline text={b.text} /></p>;
        case 'quote': return <blockquote key={i} className="whitespace-pre-line rounded-lg bg-slate-50 px-3 py-2 text-slate-600"><Inline text={b.text} /></blockquote>;
        case 'code': return <pre key={i} className="overflow-x-auto rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">{b.text}</pre>;
        case 'hr': return <hr key={i} className="border-slate-200" />;
        case 'list': return (
          <ul key={i} className="space-y-1">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-2" style={{ paddingLeft: `${it.lv * 1.25}rem` }}>
                <span className="shrink-0 text-slate-400">{it.ord || '•'}</span>
                <span className="min-w-0"><Inline text={it.text} /></span>
              </li>
            ))}
          </ul>
        );
        case 'table': return (
          <div key={i} className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>{b.rows[0]?.map((c, k) => <th key={k} className="px-3 py-2 text-left font-medium"><Inline text={c} /></th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {b.rows.slice(1).map((r, k) => <tr key={k}>{r.map((c, m) => <td key={m} className="px-3 py-2 align-top"><Inline text={c} /></td>)}</tr>)}
              </tbody>
            </table>
          </div>
        );
        default: return null;
      }
    })}
  </div>
);

const TEMPLATE_IDS: TemplateId[] = ['GRK', 'RadiARC'];

const PptGuides: React.FC<{ onUse: (template: TemplateId, version: string) => void }> = ({ onUse }) => {
  const { showToast } = useAlert();
  const [template, setTemplate] = useState<TemplateId>('GRK');
  /** 템플릿 칸에 보일 판 수 · 최신 */
  const [counts, setCounts] = useState<Partial<Record<TemplateId, { n: number; latest: string }>>>({});
  const [guides, setGuides] = useState<GuideMeta[]>([]);
  const [canUpload, setCanUpload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(null);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [secIdx, setSecIdx] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(async (keep?: string) => {
    setLoading(true);
    try {
      const all = await Promise.all(TEMPLATE_IDS.map((t) => deckApi.guides(t)));
      setCounts(Object.fromEntries(all.map((g) => [g.template, { n: g.guides.length, latest: g.guides[0]?.version ?? '' }])));
      const g = all[TEMPLATE_IDS.indexOf(template)];
      setGuides(g.guides);
      setCanUpload(g.canUpload);
      setSel(keep && g.guides.some((x) => x.version === keep) ? keep : g.guides[0]?.version ?? null);
    } catch (e) {
      showToast(`md 목록을 불러오지 못했습니다: ${(e as Error).message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, template]);

  useEffect(() => { setSecIdx(0); load(); }, [load]);

  // 고른 판의 본문 — 한 번 읽은 것은 다시 받지 않는다(템플릿마다 버전 이름이 겹치므로 「템플릿/버전」으로 기억)
  const textKey = sel ? `${template}/${sel}` : '';
  useEffect(() => {
    if (!sel || texts[textKey] !== undefined) return;
    deckApi.guideText(template, sel)
      .then((t) => setTexts((m) => ({ ...m, [textKey]: t })))
      .catch((e) => showToast(`열지 못했습니다: ${(e as Error).message}`, 'error'));
  }, [sel, texts, textKey, template, showToast]);

  const meta = guides.find((g) => g.version === sel) ?? null;
  const text = sel ? texts[textKey] : undefined;
  const sections = useMemo(() => (text ? toSections(text) : []), [text]);
  const docTitle = useMemo(() => text?.match(/^#\s+(.+)$/m)?.[1].trim() ?? '', [text]);
  const sec = sections[Math.min(secIdx, Math.max(0, sections.length - 1))];
  const blocks = useMemo(() => (sec && sec.ai !== 'no' ? parseMd(sec.body) : []), [sec]);
  const subs = blocks.filter((b): b is Extract<Block, { t: 'h' }> => b.t === 'h' && b.lv === 3);

  const pickVersion = (v: string) => { setSel(v); setSecIdx(0); };

  const upload = async () => {
    if (!file) return;
    setSending(true);
    try {
      const r = await deckApi.uploadGuide(template, file, note.trim());
      showToast(r.created ? `${template} ${r.meta.version} 을 올렸습니다` : `같은 내용이 이미 있습니다 (${template} ${r.meta.version})`, r.created ? 'success' : 'info');
      r.warnings.forEach((w) => showToast(w, 'warning'));
      setFile(null); setNote(''); setUploadOpen(false);
      load(r.meta.version);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5">
      {uploadOpen && (
        <section className="max-w-4xl bg-white rounded-lg shadow border border-indigo-200 p-5 text-sm space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-semibold text-slate-800">새 판 올리기 · {template}</h2>
            <button type="button" onClick={() => setUploadOpen(false)} className="text-slate-400 hover:text-slate-600">닫기</button>
          </div>
          {canUpload ? (
            <>
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-slate-500">
                <li>md 첫 제목에 「{template}」 와 버전 표기가 있어야 합니다 — 예: 「# {template} PPT 템플릿 — 레이아웃 규격서 (v1.1)」. 다른 템플릿 md 는 받지 않습니다</li>
                <li>「## 4. 레이아웃별 칸 규격」 · 「## 7. AI 지시문」 제목이 있어야 합니다</li>
                <li>이미 있는 버전은 받지 않습니다 — 내용을 바꿨으면 버전 표기를 올립니다. 올린 판은 지우지 않습니다</li>
              </ul>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => input.current?.click()} className="px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50">{file ? file.name : '.md 파일 고르기'}</button>
                <input ref={input} type="file" accept=".md" hidden onChange={(e) => { setFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="메모 — 무엇을 바꿨나" className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2" />
                <button type="button" onClick={upload} disabled={!file || sending} className="px-4 py-2 rounded-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">{sending ? '올리는 중…' : '올리기'}</button>
              </div>
            </>
          ) : (
            <p className="text-slate-500">운영 admin 에서만 올릴 수 있습니다. 판은 운영 · 개발이 같이 씁니다.</p>
          )}
        </section>
      )}

      <div className="grid items-start gap-5 lg:grid-cols-[20rem_minmax(0,1fr)]">
        {/* 템플릿 · 판 목록 */}
        <section className="bg-white rounded-lg shadow border border-slate-200 p-4 space-y-4 text-sm">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-semibold text-slate-800">템플릿</h2>
            <button type="button" onClick={() => setUploadOpen((v) => !v)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700">+ 새 판 올리기</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {TEMPLATE_IDS.map((t) => {
              const on = t === template;
              const c = counts[t];
              return (
                <button
                  key={t}
                  type="button"
                  aria-pressed={on}
                  onClick={() => { if (!on) { setTemplate(t); setSel(null); setUploadOpen(false); } }}
                  className={`rounded-lg px-3 py-2 text-left ${on ? 'border-2 border-indigo-500 bg-indigo-50' : 'border border-slate-200 hover:bg-slate-50'}`}
                >
                  <span className={`block font-semibold ${on ? 'text-indigo-700' : 'text-slate-700'}`}>{t}</span>
                  <span className={`block text-xs ${on ? 'text-indigo-600/80' : 'text-slate-500'}`}>{c ? `${c.n}개 판${c.latest ? ` · 최신 ${c.latest}` : ''}` : '…'}</span>
                </button>
              );
            })}
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-500">판 (최신 먼저)</div>
            {loading && guides.length === 0 ? (
              <p className="py-6 text-center text-slate-400">불러오는 중…</p>
            ) : guides.map((g, i) => (
              <button
                key={g.version}
                type="button"
                onClick={() => pickVersion(g.version)}
                className={`block w-full rounded-lg px-3 py-2.5 text-left ${g.version === sel ? 'border-2 border-indigo-500 bg-indigo-50/40' : 'border border-slate-200 hover:bg-slate-50'}`}
              >
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-slate-800">{g.version}</span>
                  {i === 0 && <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">최신 · 기본 선택</span>}
                </span>
                <span className="mt-0.5 block text-xs text-slate-500">{day(g.uploadedAt)} · {g.uploadedBy === 'seed' ? '처음 판' : g.uploadedBy}{g.revised ? ` · 개정 ${g.revised}` : ''}</span>
                {g.note && <span className="mt-0.5 block text-xs text-slate-600">{g.note}</span>}
              </button>
            ))}
          </div>
          <p className="text-xs leading-relaxed text-slate-400">판은 지우지 않습니다. 작성 화면에서 예전 판도 고를 수 있습니다. 저장 위치: grk-deck 서버 파일(DB 아님)</p>
        </section>

        {/* md 보기 */}
        <section className="bg-white rounded-lg shadow border border-slate-200 text-sm min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div className="min-w-0 space-y-0.5">
              <div className="text-xs font-medium text-indigo-600">{template} · {sel ?? '—'}</div>
              <h2 className="font-semibold text-slate-800">{docTitle || (sel ? '불러오는 중…' : '판이 없습니다')}</h2>
              {meta && <div className="text-xs text-slate-500">{day(meta.uploadedAt)} · {meta.uploadedBy === 'seed' ? '처음 판' : meta.uploadedBy}{meta.note ? ` · ${meta.note}` : ''}</div>}
            </div>
            {sel && (
              <div className="flex gap-2">
                <button type="button" onClick={() => deckApi.downloadGuide(template, sel).catch((e) => showToast(e.message, 'error'))} className="px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50">md 내려받기</button>
                <button type="button" onClick={() => onUse(template, sel)} className="px-3 py-1.5 rounded-lg font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100">이 판으로 작성 →</button>
              </div>
            )}
          </div>

          {sections.length > 0 && sec && (
            <div className="grid md:grid-cols-[16rem_minmax(0,1fr)]">
              <nav aria-label="목차" className="border-b border-slate-100 p-3 md:border-b-0 md:border-r md:max-h-[70vh] md:overflow-y-auto">
                <div className="mb-1 px-2 text-xs font-medium text-slate-500">목차</div>
                {sections.map((s, i) => (
                  <React.Fragment key={`${i}-${s.h}`}>
                    <button
                      type="button"
                      onClick={() => setSecIdx(i)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs ${s === sec ? 'bg-indigo-50 font-medium text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                      <span className="min-w-0 flex-1 truncate" title={s.h}>{s.h}</span>
                      <Tag ai={s.ai} />
                    </button>
                    {s === sec && subs.map((h) => (
                      <button key={h.id} type="button" onClick={() => document.getElementById(h.id)?.scrollIntoView({ block: 'start', behavior: 'smooth' })} className="block w-full truncate rounded-md py-1 pl-5 pr-2 text-left text-xs text-slate-500 hover:bg-slate-50" title={h.text}>
                        {h.text.replace(/`/g, '')}
                      </button>
                    ))}
                  </React.Fragment>
                ))}
              </nav>
              <article className="min-w-0 space-y-3 p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-slate-800">{sec.h}</h3>
                  <Tag ai={sec.ai} />
                </div>
                {sec.ai === 'skip' && <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">템플릿 작성(grk-deck)은 이 절을 건너뜁니다 — 채팅에서 md 를 첨부해 쓸 때의 절차입니다.</p>}
                <div className="max-h-[62vh] overflow-y-auto pr-1">
                  {sec.ai === 'no' ? <p className="text-slate-500">{sec.body}</p> : <MdView blocks={blocks} />}
                </div>
              </article>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default PptGuides;
